/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useSla.ts
 *     Feature         :   117-operational-dashboards
 *     Description     :   Composite state hook for SLA Blast Radius dashboard
 *     Constitution    :   v1.12.0 | Enterprise Dependency Architecture
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import { useState, useMemo, useCallback } from 'react';
import { useSlaStatus, useDependencies, useBlastRadius, useSimulateFailure, useSlaAlerts } from '../../services/operations/slaService';
import type { SimulationResult } from '../../services/operations/slaService';

export function useSla() {
  const [selectedCapacityId, setSelectedCapacityId] = useState<string>('');
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null);

  const statusQuery = useSlaStatus();
  const dependenciesQuery = useDependencies();
  const blastRadiusQuery = useBlastRadius(selectedCapacityId);
  const alertsQuery = useSlaAlerts();
  const simulateMutation = useSimulateFailure();

  // Status cards key off the live SKU-based capacity types (Spec 179), not the retired
  // 'Platform'/'Reference' tier names — which made the cards read 0/0.
  const hasCriticalType1Or4 = useMemo(() => {
    if (!statusQuery.data) return false;
    return statusQuery.data.some(
      s => (s.capacity_type === 'Fabric' || s.capacity_type === 'PowerBI') && s.critical_count > 0
    );
  }, [statusQuery.data]);

  const type1Status = useMemo(() => statusQuery.data?.find(s => s.capacity_type === 'Fabric'), [statusQuery.data]);
  const type4Status = useMemo(() => statusQuery.data?.find(s => s.capacity_type === 'PowerBI'), [statusQuery.data]);

  const handleSimulate = useCallback(async (capacityId: string) => {
    const result = await simulateMutation.mutateAsync(capacityId);
    setSimulationResult(result);
  }, [simulateMutation]);

  const handleSelectCapacity = useCallback((id: string) => {
    setSelectedCapacityId(id);
    setSimulationResult(null);
  }, []);

  return {
    // State
    selectedCapacityId,
    simulationResult,
    hasCriticalType1Or4,
    type1Status,
    type4Status,
    // Setters
    setSelectedCapacityId: handleSelectCapacity,
    simulateFailure: handleSimulate,
    isSimulating: simulateMutation.isPending,
    // Data
    status: statusQuery.data,
    statusLoading: statusQuery.isLoading,
    dependencies: dependenciesQuery.data,
    dependenciesLoading: dependenciesQuery.isLoading,
    blastRadius: blastRadiusQuery.data,
    blastRadiusLoading: blastRadiusQuery.isLoading,
    alerts: alertsQuery.data,
    alertsLoading: alertsQuery.isLoading,
  };
}
